import { z } from 'zod'
import { decimalSchema, positiveDecimalSchema, timestampSchema, uuidSchema } from './common.ts'
import { commercialManualExchangeRateInputSchema } from './pricing.ts'
import { decimalFitsNumeric } from './units.ts'
import { isoCurrencySchema, orderedSupplyStatusSchema } from './warehouse.ts'

const supplyMoneySchema = decimalSchema
  .refine((value) => !value.startsWith('-'), 'Сума не може бути від’ємною')
  .refine((value) => decimalFitsNumeric(value, 18, 2), 'Сума має відповідати формату numeric(18,2)')
const positiveSupplyMoneySchema = supplyMoneySchema.refine(
  (value) => positiveDecimalSchema.safeParse(value).success,
  'Сума має бути більшою за нуль',
)
const customsPercentSchema = z
  .string()
  .regex(/^(?:0|[1-9]\d?)(?:\.\d{1,4})?$|^100(?:\.0{1,4})?$/, 'Вкажіть ставку від 0 до 100')

export const foreignSupplyScheduleSchema = z.object({
  id: uuidSchema,
  supplierId: uuidSchema,
  supplierName: z.string(),
  invoiceNumber: z.string(),
  invoiceDate: z.iso.date(),
  currencyCode: isoCurrencySchema,
  invoiceAmount: supplyMoneySchema,
  paidAmount: supplyMoneySchema,
  expectedReceiptDate: z.iso.date(),
  freightToBorderUah: supplyMoneySchema,
  status: orderedSupplyStatusSchema,
  note: z.string().nullable(),
  preliminaryDutyPercent: customsPercentSchema,
  preliminaryVatPercent: customsPercentSchema,
  finalDutyPercent: customsPercentSchema,
  finalVatPercent: customsPercentSchema,
  exchangeRateSnapshotId: uuidSchema,
  requestedRateDate: z.iso.date(),
  effectiveRateDate: z.iso.date(),
  exchangeRate: decimalSchema,
  exchangeRateUnits: decimalSchema,
  exchangeRateProvider: z.enum(['NBU', 'manual']),
  unpaidAmount: decimalSchema,
  invoiceValueUah: decimalSchema,
  preliminaryDutyUah: decimalSchema,
  preliminaryVatUah: decimalSchema,
  preliminaryTotalUah: decimalSchema,
  finalDutyUah: decimalSchema,
  finalVatUah: decimalSchema,
  finalTotalUah: decimalSchema,
  customsBalanceUah: decimalSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
})
export type ForeignSupplySchedule = z.infer<typeof foreignSupplyScheduleSchema>

const foreignSupplyScheduleInputShape = {
  supplierId: uuidSchema,
  invoiceNumber: z.string().trim().min(1).max(100),
  invoiceDate: z.iso.date(),
  currencyCode: isoCurrencySchema,
  invoiceAmount: positiveSupplyMoneySchema,
  paidAmount: supplyMoneySchema,
  expectedReceiptDate: z.iso.date(),
  freightToBorderUah: supplyMoneySchema,
  status: orderedSupplyStatusSchema,
  note: z.string().trim().max(1000).nullable(),
  preliminaryDutyPercent: customsPercentSchema,
  preliminaryVatPercent: customsPercentSchema,
  finalDutyPercent: customsPercentSchema,
  finalVatPercent: customsPercentSchema,
  rateDate: z.iso.date().optional(),
  manualRate: commercialManualExchangeRateInputSchema.optional(),
}

const validateForeignSupply = (
  input: { currencyCode?: string; manualRate?: unknown },
  context: z.RefinementCtx,
) => {
  if (input.currencyCode === 'UAH' && input.manualRate)
    context.addIssue({ code: 'custom', path: ['manualRate'], message: 'Для UAH курс завжди 1:1' })
}

export const createForeignSupplyScheduleInputSchema = z
  .strictObject({
    ...foreignSupplyScheduleInputShape,
    paidAmount: supplyMoneySchema.default('0'),
    freightToBorderUah: supplyMoneySchema.default('0'),
    status: orderedSupplyStatusSchema.default('draft'),
    note: z.string().trim().max(1000).nullable().default(null),
    preliminaryDutyPercent: customsPercentSchema.default('0'),
    preliminaryVatPercent: customsPercentSchema.default('0'),
    finalDutyPercent: customsPercentSchema.default('0'),
    finalVatPercent: customsPercentSchema.default('0'),
  })
  .superRefine(validateForeignSupply)
export type CreateForeignSupplyScheduleInput = z.infer<
  typeof createForeignSupplyScheduleInputSchema
>

export const updateForeignSupplyScheduleInputSchema = z
  .strictObject(foreignSupplyScheduleInputShape)
  .partial()
  .superRefine((input, context) => {
    if (Object.keys(input).length === 0)
      context.addIssue({ code: 'custom', message: 'Передайте щонайменше одне поле' })
    validateForeignSupply(input, context)
  })
export type UpdateForeignSupplyScheduleInput = z.infer<
  typeof updateForeignSupplyScheduleInputSchema
>
